/**
 * Where a link can go in the page editor ("Goes to", "Button goes to"): one
 * entry per address, each named so it can't be mistaken for another (backlog
 * 3507a103, Prototype). A section at a page's address is listed once, under
 * Sections (b21412e4); a page at a built-in address wins over the built-in.
 */

export interface LinkDestination {
  href: string;
  label: string;
}

export interface LinkDestinationGroup {
  group: string;
  items: LinkDestination[];
}

const place = (name: string, href: string) => `${name} · ${href}`;

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

export function linkDestinations(
  pages: Array<{ path: string; title: string }>,
  collections: Array<{ slug: string; name: string }>,
  homeSlug?: string | null
): LinkDestinationGroup[] {
  const own = pages.map((p) => {
    const href = homeSlug && p.path === `/${homeSlug}` ? '/' : p.path;
    return { href, title: p.title };
  });
  const sectionAt = new Set(collections.map((c) => `/${c.slug}`));
  const pageItems = own.filter((p) => !sectionAt.has(p.href));
  const sections = collections.map((c) => {
    const href = `/${c.slug}`;
    // "Blog · /blog" and "Blog · /posts": which is the real one? Say what the
    // section is instead: "All posts · /posts".
    const named = pageItems.some((p) => sameName(p.title, c.name));
    return { href, label: place(named ? `All ${c.slug.replace(/-/g, ' ')}` : c.name, href) };
  });
  const taken = new Set([...pageItems.map((p) => p.href), ...sectionAt]);
  const builtIn = [
    { href: '/feed.xml', label: place('RSS feed', '/feed.xml') },
    { href: '/privacy', label: place('Privacy policy', '/privacy') },
  ].filter((b) => !taken.has(b.href));
  const seen = new Set<string>();
  const once = (items: LinkDestination[]) =>
    items.filter((i) => (seen.has(i.href) ? false : Boolean(seen.add(i.href))));
  return [
    {
      group: 'Pages',
      items: once(pageItems.map((p) => ({ href: p.href, label: place(p.title, p.href) }))),
    },
    { group: 'Sections', items: once(sections) },
    { group: 'Other', items: once(builtIn) },
  ].filter((g) => g.items.length);
}
